import { useState } from 'react';
import { useRouter } from 'next/router';

import { Spinner } from '@/icons';

import { DEFAULT_TOAST_OPTIONS_WARNING } from '@/components/providers';
import { useCartDispatch, useToastDispatch } from '@/lib/hooks';
import { routes } from '@/lib/routes';
import Button from '../button';
import Prose from '../prose';

import type { TypeSubscriptionCard } from './types';

export const SubscriptionCard = ({
  title,
  titleColor,
  subtitle,
  subtitleColor,
  description,
  descriptionColor,
  productId,
  quantity,
}: TypeSubscriptionCard) => {
  const [loading, setLoading] = useState(false);
  const { add, updateSubscriptionSchema } = useCartDispatch();
  const { toast } = useToastDispatch();
  const router = useRouter();

  async function convertCartToSubscription() {
    if (loading) {
      return;
    }

    setLoading(true);

    try {
      await add({ productId, quantity, gtm: { listName: 'product' } });
    } catch {
      setLoading(false);
      toast('Error al añadir el producto al carrito, intentelo más tarde.', {
        ...DEFAULT_TOAST_OPTIONS_WARNING,
      });
      return;
    }

    try {
      await updateSubscriptionSchema('2_week');
    } catch {
      setLoading(false);
      toast('Error al actualizar el carrito, intentelo más tarde.', {
        ...DEFAULT_TOAST_OPTIONS_WARNING,
      });
      return;
    }

    router.push(routes.cart);
  }

  return (
    <div className="flex flex-col justify-start gap-x-6 gap-y-4 border-[0.5px] border-primary-300 bg-white p-4 md:flex-row">
      <div className="flex flex-col gap-3 md:w-2/3 md:gap-4">
        {title && (
          <p
            style={{ color: titleColor || '#000000' }}
            className="u-headline text-xl leading-none md:text-2xl"
          >
            {title}
          </p>
        )}

        <div className="flex flex-col gap-y-1">
          {subtitle && (
            <p
              style={{ color: subtitleColor || '#B1A294' }}
              className="u-headline text-xl leading-none md:text-2xl"
            >
              {subtitle}
            </p>
          )}

          {description && (
            <Prose
              style={{ color: descriptionColor || '#000000' }}
              className="u-body text-[12px] normal-case leading-none"
              html={description}
            />
          )}
        </div>
      </div>

      <Button
        onClick={convertCartToSubscription}
        variant="tertiary"
        className="shrink-0 self-start text-white md:self-center"
      >
        {loading ? (
          <>
            <span>Añadiendo...</span>
            <Spinner className="animate-spin" />
          </>
        ) : (
          'Compra recurrente'
        )}
      </Button>
    </div>
  );
};
